import React, {Suspense, useEffect, useState} from 'react';
import {PageContainer, ProCard, ProForm, ProFormDateRangePicker, ProFormSelect,} from '@ant-design/pro-components';
import {Alert, Button, Form, Modal, Result, Space, Spin, Typography} from 'antd';
import {FileExcelOutlined, FilePptOutlined} from '@ant-design/icons';
import {useAtom, useAtomValue} from 'jotai';
import dayjs from 'dayjs';
import {accountsAtom, isGeneratingAtom, reportResultAtom, selectedAccountAtom, selectedStartDateAtom, selectedEndDateAtom} from './atoms';
import axiosApiInstance from "../../../api/axiosClient";

const AccountSelect = () => {
    const [selectedAccount, setSelectedAccount] = useAtom(selectedAccountAtom);
    const {data: accounts} = useAtomValue(accountsAtom);

    return (
        <ProFormSelect
            name="account"
            label="TikTok Account"
            rules={[{required: true, message: 'Please select an account'}]}
            options={accounts?.map(account => ({
                label: account.label,
                value: account.value
            })) || []}
            placeholder="Select TikTok account"
            fieldProps={{
                value: selectedAccount,
                onChange: (value) => setSelectedAccount(value),
            }}
        />
    );
};

const LoadingAccountSelect = () => (
    <ProFormSelect
        name="account"
        label="TikTok Account"
        placeholder="Loading accounts..."
        fieldProps={{loading: true}}
    />
);

const TikTokReports: React.FC = () => {
    const [selectedAccount] = useAtom(selectedAccountAtom);
    const [selectedStartDate, setSelectedStartDate] = useAtom(selectedStartDateAtom);
    const [selectedEndDate, setSelectedEndDate] = useAtom(selectedEndDateAtom);
    const [isGenerating, setIsGenerating] = useAtom(isGeneratingAtom);
    const [reportResult, setReportResult] = useAtom(reportResultAtom);
    const [isAnnualMode, setIsAnnualMode] = useState(false);
    const [form] = Form.useForm<any>();

    useEffect(() => {
        form.setFieldsValue({
            account: selectedAccount,
            dateRange: [dayjs(selectedStartDate), dayjs(selectedEndDate)],
        });
    }, []);

    // Generate report mutation
    const generateReport = async (values) => {
        try {
            setIsGenerating(true);
            const response = await axiosApiInstance.post('/api/tiktok-reports/generate', values);
            setIsGenerating(false);
            setReportResult(response.data);
        } catch (error) {
            setIsGenerating(false);
            console.error(error);
            setReportResult({reports: []});
        }
    }

    const onFinish = async () => {
        if (!selectedAccount || !selectedStartDate || !selectedEndDate) return;

        const start = dayjs(selectedStartDate);
        const end = isAnnualMode ? dayjs(selectedEndDate) : dayjs(selectedStartDate);
        generateReport({
            username: selectedAccount,
            month: start.month() + 1,
            year: start.year(),
            endMonth: end.month() + 1,
            endYear: end.year(),
            isAnnual: isAnnualMode,
        });
    };

    return (
        <PageContainer>
            <ProCard>
                <Alert
                    message="Generate TikTok Performance Reports"
                    description="Use Generate Reports for monthly output, or Generate Annual Report to aggregate the selected range into a single annual report."
                    type="info"
                    showIcon
                    style={{marginBottom: 24}}
                />

                <Spin spinning={isGenerating}>
                    <ProForm
                        onFinish={onFinish}
                        form={form}
                        submitter={{
                            render: (props) => {
                                return [
                                    <Space key="actions">
                                        <Button
                                            type="primary"
                                            size="large"
                                            loading={isGenerating}
                                            onClick={() => {
                                                setIsAnnualMode(false);
                                                props.form?.submit();
                                            }}
                                        >
                                            Generate Monthly
                                        </Button>
                                        <Button
                                            size="large"
                                            loading={isGenerating}
                                            onClick={() => {
                                                setIsAnnualMode(true);
                                                props.form?.submit();
                                            }}
                                        >
                                            Generate Annual
                                        </Button>
                                    </Space>
                                ];
                            },
                        }}
                    >
                        <Suspense fallback={<LoadingAccountSelect/>}>
                            <AccountSelect/>
                        </Suspense>

                        <ProFormDateRangePicker
                            name="dateRange"
                            label="Report Period"
                            rules={[
                                {required: true, message: 'Please select a date range'},
                                {
                                    validator: (_, value) => {
                                        if (!value?.[0] || !value?.[1]) return Promise.resolve();
                                        if (value[1].diff(value[0], 'month') > 11) {
                                            return Promise.reject(new Error('Range cannot exceed 12 months'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            fieldProps={{
                                picker: 'month' as any,
                                onChange: (values: any) => {
                                    if (values?.[0]) setSelectedStartDate(values[0].format('YYYY-MM'));
                                    if (values?.[1]) setSelectedEndDate(values[1].format('YYYY-MM'));
                                },
                                disabledDate: (current) => {
                                    return current && current > dayjs().subtract(2, 'day');
                                },
                            }}
                        />
                    </ProForm>
                </Spin>

                <Modal
                    open={!!reportResult?.reports?.length}
                    onOk={() => setReportResult({reports: []})}
                    closable={false}
                    maskClosable={true}
                    cancelButtonProps={{hidden: true}}
                    width={580}
                >
                    <Result
                        status="success"
                        title="Reports Generated Successfully!"
                        subTitle={
                            <Typography.Text type="secondary">
                                {reportResult?.reports?.length === 1
                                    ? '1 report has been generated and is ready to view.'
                                    : `${reportResult?.reports?.length} reports have been generated and are ready to view.`}
                            </Typography.Text>
                        }
                        extra={
                            <div style={{textAlign: 'left'}}>
                                {reportResult?.reports?.map((report) => {
                                    const label = dayjs(`${report.year}-${String(report.month).padStart(2, '0')}-01`).format('MMMM YYYY');
                                    return (
                                        <div key={`${report.year}-${report.month}`} style={{marginBottom: 12}}>
                                            <Typography.Text strong style={{display: 'block', marginBottom: 6}}>
                                                {label}
                                            </Typography.Text>
                                            <Button
                                                type="primary"
                                                icon={<FilePptOutlined/>}
                                                href={report.presentationUrl}
                                                target="_blank"
                                                style={{marginRight: 8}}
                                            >
                                                Presentation
                                            </Button>
                                            <Button
                                                icon={<FileExcelOutlined/>}
                                                href={report.spreadsheetUrl}
                                                target="_blank"
                                            >
                                                Raw Data
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        }
                    />
                </Modal>
            </ProCard>
        </PageContainer>
    );
};

export default TikTokReports;